import { useEffect, useState } from "react"
import type { FormEvent } from "react"
import { useNavigate, useParams } from "react-router"
import { useQuery } from "@tanstack/react-query"
import ModuleLayout from "../../components/admin/ModuleLayout"
import Alert from "@/components/ui/alert/Alert"
import { ConfirmDialog } from "../../components/admin/ConfirmDialog"
import { SectionHeader } from "@/components/cccd/SectionHeader"
import { cccdAdmin } from "@/lib/api/cccdAdminClient"
import { getErrorMessage } from "../../lib/errors"
import { toastError, toastSuccess } from "@/lib/toast"
import { formatCop, paymentMethodLabel } from "@/lib/cccdPayments"


function Field({ id, label, hint, children }: { id: string; label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">{label}</label>
      {children}
      {hint ? <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{hint}</p> : null}
    </div>
  )
}

export default function ManualPaymentPage() {
  const { teamId } = useParams<{ teamId: string }>()
  const navigate = useNavigate()

  const { data: team, isLoading: teamLoading } = useQuery({
    queryKey: ["cccd-team", teamId],
    queryFn: () => cccdAdmin.getTeam(teamId!),
    enabled: !!teamId,
  })

  const eventId = team?.event?.id
  const regionalId = team?.regionalEvent?.id
  const { data: plans, isLoading: plansLoading, isError: plansError } = useQuery({
    queryKey: ["cccd-event-plans", eventId ?? regionalId],
    queryFn: () => (eventId ? cccdAdmin.getEventPlans(eventId) : cccdAdmin.getRegionalPlans(regionalId!)),
    enabled: !!eventId || !!regionalId,
  })

  const [form, setForm] = useState({
    amount: "",
    planId: "",
    installmentNumber: "",
    paymentMethod: "bank_transfer_national",
    bankReference: "",
    receiptUrl: "",
    notes: "",
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")

  useEffect(() => {
    if (team?.planId) set("planId", team.planId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [team?.planId])

  function set(key: keyof typeof form, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  function goBack() {
    navigate(`/cccd/teams/${teamId}?tab=payments`)
  }

  const amountNumber = Number(form.amount)
  const amountValid = Number.isFinite(amountNumber) && amountNumber > 0
  const noPlans = !plansLoading && (plansError || !plans || plans.length === 0)
  const selectedPlan = plans?.find((p) => p.id === form.planId)
  const [confirmOpen, setConfirmOpen] = useState(false)

  // Registrar un pago tiene efecto contable y no se puede deshacer desde aquí:
  // el submit solo abre el resumen, el envío real ocurre al confirmarlo.
  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (!teamId || !amountValid || !form.planId) return
    setError("")
    setConfirmOpen(true)
  }

  async function register() {
    if (!teamId) return
    setLoading(true)
    setError("")
    try {
      await cccdAdmin.registerPayment(teamId, {
        amount: Number(form.amount),
        planId: form.planId,
        installmentNumber: form.installmentNumber ? Number(form.installmentNumber) : undefined,
        paymentMethod: form.paymentMethod,
        bankReference: form.bankReference || undefined,
        receiptUrl: form.receiptUrl || undefined,
        notes: form.notes || undefined,
      })
      toastSuccess("Pago registrado. Quedó pendiente de revisión.")
      goBack()
    } catch (err) {
      const message = getErrorMessage(err) || "Error al registrar el pago."
      setError(message)
      toastError(message)
      setLoading(false)
      setConfirmOpen(false)
    }
  }

  const inputCls = "w-full rounded-lg border border-gray-300 p-2 text-sm dark:border-gray-700 dark:bg-gray-900"

  return (
    <ModuleLayout
      breadcrumbs={[
        { label: "SateLab", to: "/" },
        { label: "CCCD", to: "/cccd" },
        ...(team ? [{ label: team.name, to: `/cccd/teams/${teamId}` }] : []),
        "Registrar pago",
      ]}
      title="Registrar pago manual"
      subtitle={
        teamLoading
          ? "Cargando equipo..."
          : team
            ? `Pago para "${team.name}" — recibido fuera de Wompi (transferencia, efectivo, etc.)`
            : "Pago recibido fuera de Wompi (transferencia, efectivo, etc.)"
      }
      tabs={[]}
      metrics={[]}
    >
      <form onSubmit={handleSubmit} className="max-w-2xl">
        <SectionHeader
          title="Pago manual"
          subtitle="Monto, plan y método — registrado fuera de la pasarela Wompi"
          icon={<svg width="17" height="17" viewBox="0 0 24 24" fill="none"><rect x="3" y="6" width="18" height="12" rx="2" stroke="currentColor" strokeWidth="1.7" /><path d="M3 10h18" stroke="currentColor" strokeWidth="1.7" /></svg>}
          className="space-y-3"
        >
          {noPlans ? (
            <Alert
              variant="error"
              title="Este evento no tiene planes de pago"
              message="No se cargaron planes para el evento del equipo. Configúralos en la pestaña Planes del evento y vuelve a intentar; sin plan no se puede registrar el pago."
            />
          ) : null}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field id="manual-payment-amount" label="Monto recibido (COP)" hint={amountValid ? formatCop(amountNumber) : "Sin puntos ni comas, ej. 350000"}>
              <input id="manual-payment-amount" required type="number" inputMode="numeric" min={1} step={1} className={inputCls}
                value={form.amount} onChange={(e) => set("amount", e.target.value)} />
            </Field>
            <Field id="manual-payment-plan" label="Plan de pago">
              <select id="manual-payment-plan" required disabled={plansLoading || noPlans} className={inputCls} value={form.planId} onChange={(e) => set("planId", e.target.value)}>
                <option value="" disabled>{plansLoading ? "Cargando planes..." : "Selecciona el plan de pago..."}</option>
                {(plans ?? []).map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field id="manual-payment-installment" label="Número de cuota (opcional)">
              <input id="manual-payment-installment" type="number" inputMode="numeric" min={1} step={1} className={inputCls}
                value={form.installmentNumber} onChange={(e) => set("installmentNumber", e.target.value)} />
            </Field>
            <Field id="manual-payment-method" label="Método de pago">
              <select id="manual-payment-method" required className={inputCls} value={form.paymentMethod} onChange={(e) => set("paymentMethod", e.target.value)}>
                <option value="bank_transfer_national">Transferencia nacional</option>
                <option value="bank_transfer_international">Transferencia internacional</option>
                <option value="wompi">Wompi (reconciliación manual — el webhook no confirmó)</option>
              </select>
            </Field>
          </div>
          <Field id="manual-payment-reference" label="Referencia bancaria (opcional)">
            <input id="manual-payment-reference" type="text" className={inputCls}
              value={form.bankReference} onChange={(e) => set("bankReference", e.target.value)} />
          </Field>
          <Field id="manual-payment-receipt" label="URL del comprobante (opcional)">
            <input id="manual-payment-receipt" type="url" className={inputCls}
              value={form.receiptUrl} onChange={(e) => set("receiptUrl", e.target.value)} />
          </Field>
          <Field id="manual-payment-notes" label="Notas internas (opcional)">
            <textarea id="manual-payment-notes" rows={2} className={inputCls}
              value={form.notes} onChange={(e) => set("notes", e.target.value)} />
          </Field>
        </SectionHeader>

        {error ? <div className="mt-5"><Alert variant="error" title="No se pudo registrar el pago" message={error} /></div> : null}

        <div className="mt-5 flex justify-end gap-2 border-t border-gray-100 pt-5 dark:border-gray-800">
          <button type="button" onClick={goBack} className="min-h-11 rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-800">
            Cancelar
          </button>
          <button type="submit" disabled={loading || noPlans || plansLoading} className="min-h-11 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-60">
            Revisar y registrar
          </button>
        </div>
      </form>

      <ConfirmDialog
        isOpen={confirmOpen}
        danger={false}
        confirming={loading}
        confirmingLabel="Registrando..."
        title="¿Registrar este pago?"
        message={`${formatCop(amountNumber)} para "${team?.name ?? "el equipo"}" · ${selectedPlan?.name ?? "plan seleccionado"} · ${paymentMethodLabel(form.paymentMethod)}${form.installmentNumber ? ` · cuota ${form.installmentNumber}` : ""}${form.bankReference ? ` · ref. ${form.bankReference}` : ""}. Revisa el monto: no se puede deshacer desde aquí.`}
        confirmLabel="Registrar pago"
        onConfirm={() => void register()}
        onCancel={() => !loading && setConfirmOpen(false)}
      />
    </ModuleLayout>
  )
}
